import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";
import { MUNICIPIOS, SERVICE_META } from "../data/content";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
    nombre: "", email: "", telefono: "", municipio: "", nombre_perro: "",
    edad_perro: "", servicio: "", fechas: "", mensaje: "",
};

const ContactForm = ({ servicioInicial = "" }) => {
    const [form, setForm] = useState({ ...initial, servicio: servicioInicial });
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const { t } = useLang();
    const f = t.contact.form;

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/solicitudes`, form);
            setDone(true);
            toast.success(f.toastOk);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : f.toastErr);
        } finally {
            setSending(false);
        }
    };

    if (done) {
        return (
            <div
                data-testid="contact-success"
                className="rounded-[2rem] border border-olive/30 bg-olive-light/60 p-10 text-center"
            >
                <CheckCircle2 className="mx-auto text-olive" size={44} />
                <h3 className="font-serif mt-4 text-2xl font-semibold text-ink sm:text-3xl">
                    {t.contact.success.title}
                </h3>
                <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-[#525B53]">
                    {t.contact.success.text1} {form.nombre_perro || "tu perro"} {t.contact.success.text2}
                </p>
                <button
                    data-testid="contact-reset-button"
                    onClick={() => { setDone(false); setForm({ ...initial, servicio: servicioInicial }); }}
                    className="mt-6 rounded-full border border-olive/40 px-5 py-2.5 text-sm font-semibold text-olive-dark transition-colors hover:bg-olive-light"
                >
                    {t.contact.success.reset}
                </button>
            </div>
        );
    }

    return (
        <form data-testid="contact-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
            <div>
                <label htmlFor="f-nombre" className="mb-1.5 block text-[13px] font-semibold text-ink">{f.nombre}</label>
                <input id="f-nombre" data-testid="input-nombre" required minLength={2} value={form.nombre} onChange={set("nombre")} className="field-input" placeholder={f.nombrePh} />
            </div>
            <div>
                <label htmlFor="f-email" className="mb-1.5 block text-[13px] font-semibold text-ink">{f.email}</label>
                <input id="f-email" data-testid="input-email" type="email" required value={form.email} onChange={set("email")} className="field-input" placeholder={f.emailPh} />
            </div>
            <div>
                <label htmlFor="f-telefono" className="mb-1.5 block text-[13px] font-semibold text-ink">{f.telefono}</label>
                <input id="f-telefono" data-testid="input-telefono" type="tel" value={form.telefono} onChange={set("telefono")} className="field-input" placeholder={f.telefonoPh} />
            </div>
            <div>
                <label htmlFor="f-municipio" className="mb-1.5 block text-[13px] font-semibold text-ink">{f.municipio}</label>
                <select id="f-municipio" data-testid="input-municipio" required value={form.municipio} onChange={set("municipio")} className="field-input">
                    <option value="" disabled>{f.municipioPh}</option>
                    {MUNICIPIOS.map((m) => <option key={m} value={m}>{m}</option>)}
                    <option value="Otro">{f.otro}</option>
                </select>
            </div>
            <div>
                <label htmlFor="f-perro" className="mb-1.5 block text-[13px] font-semibold text-ink">{f.perro}</label>
                <input id="f-perro" data-testid="input-nombre-perro" required value={form.nombre_perro} onChange={set("nombre_perro")} className="field-input" placeholder={f.perroPh} />
            </div>
            <div>
                <label htmlFor="f-edad" className="mb-1.5 block text-[13px] font-semibold text-ink">{f.edad}</label>
                <input id="f-edad" data-testid="input-edad-perro" value={form.edad_perro} onChange={set("edad_perro")} className="field-input" placeholder={f.edadPh} />
            </div>
            <div>
                <label htmlFor="f-servicio" className="mb-1.5 block text-[13px] font-semibold text-ink">{f.servicio}</label>
                <select id="f-servicio" data-testid="select-servicio" required value={form.servicio} onChange={set("servicio")} className="field-input">
                    <option value="" disabled>{f.servicioPh}</option>
                    {SERVICE_META.map((m) => <option key={m.slug} value={m.slug}>{t.services[m.slug].nombre}</option>)}
                    <option value="otro">{f.servicioOtro}</option>
                </select>
            </div>
            <div>
                <label htmlFor="f-fechas" className="mb-1.5 block text-[13px] font-semibold text-ink">{f.fechas}</label>
                <input id="f-fechas" data-testid="input-fechas" value={form.fechas} onChange={set("fechas")} className="field-input" placeholder={f.fechasPh} />
            </div>
            <div className="sm:col-span-2">
                <label htmlFor="f-mensaje" className="mb-1.5 block text-[13px] font-semibold text-ink">{f.mensaje}</label>
                <textarea id="f-mensaje" data-testid="input-mensaje" rows={4} value={form.mensaje} onChange={set("mensaje")} className="field-input resize-none" placeholder={f.mensajePh} />
            </div>
            <div className="sm:col-span-2">
                <button
                    type="submit"
                    data-testid="submit-button"
                    disabled={sending}
                    className="group flex w-full items-center justify-center gap-2 rounded-full bg-terracotta px-8 py-4 text-[15px] font-bold text-cream shadow-lg shadow-terracotta/25 transition-[background-color,transform] duration-200 hover:bg-terracotta-dark hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                    {sending ? f.sending : f.submit}
                    <Send size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                </button>
                <p className="mt-3 text-xs text-[#7C877E]">{f.privacy}</p>
            </div>
        </form>
    );
};

export default ContactForm;
